import { HsaPdpWidgetPlacement } from "@redotech/redo-conversion-core-common/conversion/hsa-autocheck";
import { InsertionPosition } from "@redotech/redo-storefront-common/insertion-position";
import { useEffect, useRef, useState } from "react";
import { useLeakyDebounce } from "../../utils/use-leaky-debounce";
import { RedoHsaPdpContainerElement } from "./element";

export const HSA_PDP_CONTAINER_ID = "redo-hsa-pdp-container";

export interface HsaPdpPortal {
  foundSelector: string;
  portal: RedoHsaPdpContainerElement;
}

const positionMap: Record<InsertionPosition, InsertPosition> = {
  [InsertionPosition.BEFORE]: "beforebegin",
  [InsertionPosition.AFTER]: "afterend",
  [InsertionPosition.PREPEND]: "afterbegin",
  [InsertionPosition.APPEND]: "beforeend",
};

/**
 * Places the HSA/FSA badge host at every element matching the configured
 * selector, re-inserting on theme re-renders and pruning stale hosts.
 */
export function useHsaPdpPortals({
  placement,
}: {
  placement: HsaPdpWidgetPlacement;
}): HsaPdpPortal[] {
  const [tick, setTick] = useState(Symbol());
  const debouncedTick = useLeakyDebounce(tick, { outputHz: 4 });

  const { cssSelector } = placement;
  const insertionPosition =
    placement.insertionPosition ?? InsertionPosition.APPEND;

  useEffect(() => {
    const observer = new MutationObserver(() => setTick(Symbol()));
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  const trackedRef = useRef(new Map<Element, RedoHsaPdpContainerElement>());
  const [portals, setPortals] = useState<HsaPdpPortal[]>([]);

  useEffect(() => {
    const tracked = trackedRef.current;

    if (!cssSelector) {
      if (tracked.size > 0) {
        for (const element of tracked.values()) {
          element.remove();
        }
        tracked.clear();
        setPortals([]);
      }
      return;
    }

    RedoHsaPdpContainerElement.register();

    for (const [target, element] of tracked) {
      if (!target.isConnected || !element.isConnected) {
        element.remove();
        tracked.delete(target);
      }
    }

    const next: HsaPdpPortal[] = [];
    for (const target of document.querySelectorAll(cssSelector)) {
      let element = tracked.get(target);
      if (!element) {
        element = document.createElement(
          RedoHsaPdpContainerElement.TAG,
        ) as RedoHsaPdpContainerElement;
        element.id = HSA_PDP_CONTAINER_ID;
        element.style.setProperty("display", "unset", "important");
        target.insertAdjacentElement(positionMap[insertionPosition], element);
        tracked.set(target, element);
      }
      next.push({ foundSelector: cssSelector, portal: element });
    }

    setPortals((prev) => (samePortals(prev, next) ? prev : next));
  }, [cssSelector, insertionPosition, debouncedTick]);

  useEffect(
    () => () => {
      for (const element of trackedRef.current.values()) {
        element.remove();
      }
      trackedRef.current.clear();
    },
    [],
  );

  return portals;
}

function samePortals(a: HsaPdpPortal[], b: HsaPdpPortal[]): boolean {
  if (a.length !== b.length) {
    return false;
  }
  for (const [i, element] of a.entries()) {
    const other = b[i]!;
    if (
      element.portal !== other.portal ||
      element.foundSelector !== other.foundSelector
    ) {
      return false;
    }
  }
  return true;
}
